/**
 * The presentational member row: avatar, name, `@username`, role label,
 * status pill, lives, playing-now indicator, the two-number medal line, and
 * the overflow control. It takes a prepared row and draws it, and it decides
 * nothing. Every contest field (`pill`, `isPlayingNow`, `medalLine`) renders
 * nothing at all when absent, never a placeholder.
 */
import { Button, cn, HIT_SLOP, Skeleton } from "@sportsmet/ui";
import { type ReactNode } from "react";

import type { typeMemberPillTone } from "./memberRowFacts";
import type { typeRosterMemberRow } from "./membersRosterModel";

export type typePoolMemberRowProps = {
  row: typeRosterMemberRow;
  onOpenActions: (userId: string) => void;
  /**
   * Opens this member's pool-scoped profile (ISSUE-194). Required, not
   * optional: an optional handler would let a future row ship with no way into
   * the profile and nobody would notice it was missing.
   */
  onOpenDetail: (userId: string) => void;
};

/** The accessible name of the control that opens a member's pool-scoped
 *  profile. The visible text is just the name, which does not say what
 *  clicking it does. */
export function openDetailLabel(displayName: string): string {
  return `See ${displayName} in this pool`;
}

export const PLAYING_NOW_LABEL = "Playing now";
export const MEMBER_SKELETON_ROW_COUNT = 5;

export const MEMBER_PILL_TONE_CLASS: Record<typeMemberPillTone, string> = {
  success: "bg-success-tint text-success",
  accent: "bg-accent-tint text-accent",
  warning: "bg-warning-tint text-warning",
  error: "bg-error-tint text-error",
};

export const PoolMemberRow = ({
  row,
  onOpenActions,
  onOpenDetail,
}: typePoolMemberRowProps): ReactNode => {
  return (
    <li
      className="flex items-center justify-between gap-3"
      data-testid="pool-member-row"
      data-user-id={row.userId}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {row.avatarUrl !== null && row.avatarUrl !== "" ? (
          <img
            src={row.avatarUrl}
            alt=""
            className="size-8 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="grid size-8 shrink-0 place-items-center rounded-full bg-ink-secondary text-caption font-bold text-ink-inverse"
          >
            {row.initials}
          </span>
        )}
        <div className="flex min-w-0 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            {/* The name opens the profile; `More` opens the actions dialog.
                Two affordances, two targets. The row itself is not the button:
                it already contains the overflow control, and nesting one
                interactive element inside another is invalid and wrecks
                keyboard order. The name's text is shorter than the 44px tap
                floor, so it carries the same hit-slop overlay the compact
                `Button` size uses (packages/ui/src/lib/focus.ts). */}
            <button
              type="button"
              onClick={() => {
                onOpenDetail(row.userId);
              }}
              aria-label={openDetailLabel(row.displayName)}
              data-testid="pool-member-open-detail"
              className={cn(
                "relative text-left text-body-strong text-ink underline-offset-2 hover:underline",
                HIT_SLOP,
              )}
            >
              {row.displayName}
            </button>
            {row.usernameLabel === null ? null : (
              <span className="text-caption text-ink-secondary">
                {row.usernameLabel}
              </span>
            )}
            {row.roleLabel === null ? null : (
              <span className="text-caption text-ink-secondary">
                {row.roleLabel}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {row.pill === null ? null : (
              <span
                data-testid="pool-member-pill"
                className={`rounded-md px-2 py-1 text-caption ${MEMBER_PILL_TONE_CLASS[row.pill.tone]}`}
              >
                {row.pill.label}
              </span>
            )}
            {row.isPlayingNow ? (
              <span
                data-testid="pool-member-playing-now"
                className="text-caption text-ink-secondary"
              >
                {PLAYING_NOW_LABEL}
              </span>
            ) : null}
            {row.medalLine === null ? null : (
              <span
                data-testid="pool-member-medals"
                className="text-caption text-ink-secondary"
              >
                {row.medalLine}
              </span>
            )}
          </div>
        </div>
      </div>
      <Button
        variant="ghost"
        size="md"
        className="shrink-0"
        data-testid="pool-member-overflow"
        aria-label={`More actions for ${row.displayName}`}
        onClick={() => {
          onOpenActions(row.userId);
        }}
      >
        More
      </Button>
    </li>
  );
};

export const PoolMemberRowSkeleton = (): ReactNode => {
  return (
    <ul data-testid="pool-members-skeleton">
      {Array.from({ length: MEMBER_SKELETON_ROW_COUNT }, (_, index) => (
        <li key={index}>
          <Skeleton
            shape="text"
            label={index === 0 ? "Loading members" : undefined}
          />
        </li>
      ))}
    </ul>
  );
};
